import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTheme } from "../../context/ThemeContext";
import {
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getLightBorder,
  getTableRowStyles,
} from "../../utils/themeUtils";
import Tooltip from "../common/Tooltip";
import {
  AttendanceContextType,
  RosterAction,
  RosterStatus,
  capabilitiesFor,
  isMarkAllowed,
} from "./attendanceContexts";
import { RosterFlagBadges, RosterFlagsScope, useRosterFlagsScope } from "./RosterFlags";
import { RosterFlagsFetcher } from "../../api/rosterFlagsAPI";
import { useConfirmModal } from "../../hooks/useConfirmModal";

/**
 * THE attendance roster — class, event, camp and clinic mark attendance here
 * (Phase D2, backend docs/EVENT_PRIMITIVE_CONSOLIDATION.md "D2 — frontend").
 *
 * This is the class roster (it was the body of staff/attendance/table/
 * AttendanceTable) with the context as a parameter: every difference between
 * the contexts is a flag in attendanceContexts.ts, and every write goes
 * through the `save` the context passes — the roster never knows a route.
 *
 * The class page renders through here unchanged; its DOM is pinned by
 * staff/attendance/table/__tests__/AttendanceTable.dom.test.tsx, recorded
 * before the extraction.
 */

export interface RosterRow {
  id: string;
  firstName?: string;
  lastName?: string;
  isTrial?: boolean;
  account_flagged?: boolean;
  account_flag_reason?: string | null;
  [key: string]: any;
}

export interface RosterMark {
  status: RosterStatus;
  /** Event contexts: the check-in / check-out stamps (ISO). */
  checkedInAt?: string | null;
  checkedOutAt?: string | null;
  [key: string]: any;
}

export interface SaveResult {
  ok: boolean;
  /** The backend's own reason, shown instead of the generic wording. */
  message?: string;
}

export interface AttendanceRosterProps {
  context: AttendanceContextType;
  rows: RosterRow[];
  attendance: Record<string, RosterMark | undefined>;
  updateAttendanceLocally: (rowId: string, status: RosterStatus) => void;
  /** Persist one click. The roster has already updated the row optimistically. */
  save: (rowId: string, action: RosterAction, row: RosterRow) => Promise<SaveResult>;
  /** After a successful save (event contexts re-read their stamps). */
  onSaved?: (rowId: string, action: RosterAction) => void;
  /** `absentIsFinal` contexts: ask before a terminal absence. */
  confirmAbsent?: (row: RosterRow) => Promise<boolean>;
  renderName: (row: RosterRow) => React.ReactNode;
  renderDetails?: (row: RosterRow, mark: RosterMark | undefined) => React.ReactNode;
  /** `rowDetails` contexts: a row click. */
  onRowClick?: (row: RosterRow) => void;
  itemsPerPage: number;
  nameHeader?: string;
  emptyText?: string;
  /** Rendered after the list, inside the roster (the class details modal). */
  children?: React.ReactNode;
  /**
   * Who a row IS, for the medical / no-photos / waiver-missing badges
   * (RosterFlags): a class row is an athlete, an event row a registration.
   * Return null for a row with neither (a trial attendee). Every context
   * passes this, so the badges are the same on every roster.
   */
  flagSubject?: (row: RosterRow) => RosterFlagSubject | null;
  /**
   * Everyone on the sheet, when `rows` is a filtered view of it. The flags
   * are read once for the whole sheet so typing in a search box does not
   * re-ask for them.
   */
  flagRows?: RosterRow[];
  /**
   * How the flags are read, for a screen that is not signed in as staff (the
   * kiosk). Left out, the staff endpoint is used.
   */
  flagFetcher?: RosterFlagsFetcher;
}

export interface RosterFlagSubject {
  athleteId?: string | null;
  registrationId?: string | null;
}

const AttendanceRoster: React.FC<AttendanceRosterProps> = ({
  context,
  rows,
  attendance,
  updateAttendanceLocally,
  save,
  onSaved,
  confirmAbsent,
  renderName,
  renderDetails,
  onRowClick,
  itemsPerPage,
  nameHeader = "Athlete Name",
  emptyText = "No memberships found.",
  children,
  flagSubject,
  flagRows,
  flagFetcher,
}) => {
  const { isDarkMode } = useTheme();
  const caps = capabilitiesFor(context);
  const { confirm, confirmModal } = useConfirmModal();

  // A make-up counts as ATTENDED. On an enrolled row (a make-up recorded
  // before they joined this class, or in their own class) it shows as
  // Present; it used to show neither mark and read as an absence (2026-10-11).
  const shownStatus = (mark: RosterMark | undefined): RosterStatus | undefined =>
    caps.makeups && mark?.status === "makeup" ? "present" : mark?.status;
  // A makeup VISITOR is not enrolled here: the row carries the make-up and its
  // Undo, not Present/Absent (the class takes no other mark for them).
  const isMakeupVisitor = (row: RosterRow) => caps.makeups && row?.isMakeupVisitor === true;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [savingRowId, setSavingRowId] = useState<string | null>(null);

  // One flags read for the whole sheet (see RosterFlags).
  const flagSubjects = useMemo(() => {
    const athleteIds: string[] = [];
    const registrationIds: string[] = [];
    if (flagSubject) {
      (flagRows ?? rows ?? []).forEach((row) => {
        const subject = flagSubject(row);
        if (subject?.registrationId) registrationIds.push(subject.registrationId);
        else if (subject?.athleteId) athleteIds.push(subject.athleteId);
      });
    }
    return { athleteIds, registrationIds };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- flagSubject is a pure mapping the context re-creates every render
  }, [rows, flagRows]);

  // The flags, the one-line summary above the sheet and its filter. Read
  // here (not inside RosterFlagsScope) because the sheet is paged: a filter
  // must narrow the whole list BEFORE it is cut into pages, or "show the 2
  // medical alerts" would find only the ones on the page being looked at.
  const shownSubjects = useMemo(() => {
    const athleteIds: string[] = [];
    const registrationIds: string[] = [];
    if (flagSubject) {
      (rows ?? []).forEach((row) => {
        const subject = flagSubject(row);
        if (subject?.registrationId) registrationIds.push(subject.registrationId);
        else if (subject?.athleteId) athleteIds.push(subject.athleteId);
      });
    }
    return { athleteIds, registrationIds };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- flagSubject is a pure mapping the context re-creates every render
  }, [rows]);
  const flagScope = useRosterFlagsScope(
    { athleteIds: flagSubjects.athleteIds, registrationIds: flagSubjects.registrationIds, shown: shownSubjects },
    flagFetcher
  );
  const flagFilter = flagScope.filter;
  const matchesFlagFilter = flagScope.matches;
  const allRows = rows;
  const visibleRows = useMemo(
    () => (flagFilter && flagSubject ? (allRows ?? []).filter((row) => matchesFlagFilter(flagSubject(row))) : allRows),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- flagSubject: see above
    [allRows, flagFilter, matchesFlagFilter]
  );
  // A filter changes how many pages there are: start again from the first.
  useEffect(() => {
    setCurrentPage(1);
  }, [flagFilter]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = visibleRows?.slice(indexOfFirstItem, indexOfLastItem);

  // "Mark All Present" SAVES each row on the WHOLE roster for this session
  // (every page, not only the one on screen — 2026-10-11) that is not already
  // present, through the same `save` a click uses — optimistic, reverted on
  // failure. Staff confirm first, with the count. Until 2026-09-24 it only
  // changed the screen. Unchecking only clears the box; it never marks anyone
  // absent in bulk.
  const handleSelectAllChange = async (checked: boolean) => {
    setSelectAllChecked(checked);
    if (!checked) return;

    const targets = (visibleRows ?? []).filter((row) => {
      if (isMakeupVisitor(row)) return false;
      const previous = shownStatus(attendance[row.id]);
      return previous !== "present" && isMarkAllowed(caps, previous, "present");
    });
    if (targets.length === 0) return;
    const ok = await confirm({
      title: "Mark everyone present?",
      message: `Mark ${targets.length} ${targets.length === 1 ? "person" : "people"} present for this session? This covers everyone on the roster not already marked present, on every page.`,
      confirmText: `Mark ${targets.length} present`,
    });
    if (!ok) {
      setSelectAllChecked(false);
      return;
    }

    const previousById = new Map(targets.map((row) => [row.id, attendance[row.id]?.status]));
    targets.forEach((row) => updateAttendanceLocally(row.id, "present"));

    const results = await Promise.allSettled(targets.map((row) => save(row.id, "present", row)));
    let failed = 0;
    results.forEach((outcome, i) => {
      const row = targets[i];
      const ok = outcome.status === "fulfilled" && outcome.value?.ok;
      if (ok) {
        onSaved?.(row.id, "present");
      } else {
        failed += 1;
        updateAttendanceLocally(row.id, (previousById.get(row.id) as RosterStatus) ?? "no_record");
      }
    });
    if (failed > 0) {
      toast.error(`${failed} of ${targets.length} could not be marked present`);
      setSelectAllChecked(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle attendance button click with auto-save.
  // Clicking the status a row already has UNMARKS it (back to no_record) —
  // staff were otherwise stuck with an accidental mark and had no way to
  // clear it (Stacy, 2026-07-12). Whether a context can clear, switch or
  // must confirm is attendanceContexts' call.
  const handleAttendanceClick = async (row: RosterRow, status: "present" | "absent") => {
    const rowId = row.id;
    // Prevent clicking while already saving
    if (savingRowId === rowId) return;

    const previousStatus = attendance[rowId]?.status;
    if (!isMarkAllowed(caps, shownStatus(attendance[rowId]), status)) return;
    const isUnmarking = shownStatus(attendance[rowId]) === status;

    if (!isUnmarking && status === "absent" && caps.absentIsFinal && confirmAbsent) {
      if (!(await confirmAbsent(row))) return;
    }

    try {
      setSavingRowId(rowId);

      // Update local state immediately for better UX
      updateAttendanceLocally(rowId, isUnmarking ? "no_record" : status);

      const result = await save(rowId, isUnmarking ? "clear" : status, row);

      if (!result?.ok) {
        // Revert on failure
        updateAttendanceLocally(
          rowId,
          isUnmarking ? (previousStatus as RosterStatus) : previousStatus ?? "no_record"
        );
        toast.error(
          result?.message || (isUnmarking ? "Failed to clear attendance" : "Failed to save attendance")
        );
        return;
      }
      onSaved?.(rowId, isUnmarking ? "clear" : status);
    } catch (error) {
      updateAttendanceLocally(rowId, previousStatus ?? "no_record");
      toast.error(isUnmarking ? "Error clearing attendance" : "Error saving attendance");
    } finally {
      setSavingRowId(null);
    }
  };

  const handleCheckOut = async (row: RosterRow) => {
    if (savingRowId === row.id) return;
    try {
      setSavingRowId(row.id);
      const result = await save(row.id, "check_out", row);
      if (!result?.ok) {
        toast.error(result?.message || "Failed to check out");
        return;
      }
      onSaved?.(row.id, "check_out");
    } catch (error) {
      toast.error("Error checking out");
    } finally {
      setSavingRowId(null);
    }
  };

  const presentTooltip = (mark: RosterMark | undefined) => {
    const status = shownStatus(mark);
    if (mark?.status === "makeup" && status === "present") {
      return "Recorded as a makeup: counts as attended. Click again to clear this mark";
    }
    if (status === "present") return caps.clearPresent ? "Click again to clear this mark" : "Marked present";
    if (status === "absent" && !isMarkAllowed(caps, status, "present")) {
      return "Marked as a no-show — this cannot be changed here";
    }
    return "Mark present";
  };

  const absentTooltip = (mark: RosterMark | undefined) => {
    const status = shownStatus(mark);
    if (status === "absent") {
      if (caps.clearAbsent) return "Click again to clear this mark";
      return caps.absentIsFinal ? "Marked as a no-show" : "Marked absent — mark present to change it";
    }
    if (status === "present" && !caps.switchPresentAbsent) return "Clear the present mark first";
    return caps.absentIsFinal ? "Mark as a no-show (cannot be undone)" : "Mark absent";
  };

  // Undo a makeup visitor's makeup: the same clear a second click on a mark
  // does (the class deletes the record).
  const handleUndoMakeup = async (row: RosterRow) => {
    if (savingRowId === row.id) return;
    const previousStatus = attendance[row.id]?.status;
    try {
      setSavingRowId(row.id);
      updateAttendanceLocally(row.id, "no_record");
      const result = await save(row.id, "clear", row);
      if (!result?.ok) {
        updateAttendanceLocally(row.id, (previousStatus as RosterStatus) ?? "makeup");
        toast.error(result?.message || "Failed to undo makeup");
        return;
      }
      onSaved?.(row.id, "clear");
    } catch (error) {
      updateAttendanceLocally(row.id, (previousStatus as RosterStatus) ?? "makeup");
      toast.error("Error undoing makeup");
    } finally {
      setSavingRowId(null);
    }
  };

  const makeupBadge = (
    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300">
      Makeup
    </span>
  );

  const renderRow = (row: RosterRow) => {
    const mark = attendance[row.id];
    const shown = shownStatus(mark);
    const saving = savingRowId === row.id;
    const presentBlocked = saving || !isMarkAllowed(caps, shown, "present");
    const absentBlocked = saving || !isMarkAllowed(caps, shown, "absent");
    const canCheckOut =
      caps.checkInOut && mark?.status === "present" && !!mark?.checkedInAt && !mark?.checkedOutAt;

    return (
      <tr
        className={`border-b ${getLightBorder(isDarkMode)}${caps.rowDetails ? " cursor-pointer" : ""} ${getTableRowStyles(isDarkMode)}`}
        key={row?.id}
        onClick={caps.rowDetails ? () => onRowClick?.(row) : undefined}
      >
        {/* Member Name */}
        <td className={`px-6 py-4 w-1/2 ${row?.account_flagged ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              {row?.account_flagged && (
                <span className="relative flex h-3 w-3 flex-shrink-0" title={row.account_flag_reason || "Account flagged"}>
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
                </span>
              )}
              {renderName(row)}
              {flagSubject && <RosterFlagBadges {...(flagSubject(row) || {})} />}
            </div>

            {renderDetails?.(row, mark)}
          </div>
        </td>

        {/* Action */}
        <td className="px-6 py-4 w-1/2" onClick={(e) => e.stopPropagation()}>
          {isMakeupVisitor(row) ? (
          <div className="flex items-center gap-2">
            <Tooltip content="Here on a makeup for this session: counts as attended">
              {makeupBadge}
            </Tooltip>
            <button
              onClick={() => handleUndoMakeup(row)}
              disabled={saving}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              } ${saving ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              Undo Makeup
            </button>
          </div>
          ) : (
          <div className="flex gap-2">
            <Tooltip content={presentTooltip(mark)}>
              <button
                onClick={() => handleAttendanceClick(row, "present")}
                disabled={presentBlocked}
                aria-pressed={shown === "present"}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                  shown === "present"
                    ? "bg-green-600 text-white shadow-md"
                    : isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                } ${presentBlocked ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                Present
              </button>
            </Tooltip>
            <Tooltip content={absentTooltip(mark)}>
              <button
                onClick={() => handleAttendanceClick(row, "absent")}
                disabled={absentBlocked}
                aria-pressed={shown === "absent"}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                  shown === "absent"
                    ? "bg-red-600 text-white shadow-md"
                    : isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                } ${absentBlocked ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                Absent
              </button>
            </Tooltip>
            {canCheckOut && (
              <Tooltip content="Record when they left">
                <button
                  onClick={() => handleCheckOut(row)}
                  disabled={saving}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-all border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 ${
                    saving ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Check Out
                </button>
              </Tooltip>
            )}
            {caps.makeups && mark?.status === "makeup" && makeupBadge}
          </div>
          )}
        </td>
      </tr>
    );
  };

  const tableHead = (
    <thead className={`text-xs uppercase font-semibold ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} ${getPrimaryText(isDarkMode)}`}>
      <tr>
        <th scope="col" className="px-6 py-3 w-1/2 cursor-pointer">
          {nameHeader}
        </th>
        <th scope="col" className="px-6 py-3 w-1/2 cursor-pointer">
          Actions
        </th>
      </tr>
    </thead>
  );

  return (
    <RosterFlagsScope scope={flagScope}>
    <div>
      {rows?.length > 0 ? (
        <div>
          {caps.bulkMarkPresent && (
            // Select All Checkbox with label
            <div className="flex items-center gap-2 mb-4">
              <input
                id="selectAll"
                type="checkbox"
                checked={selectAllChecked}
                onChange={(e) => handleSelectAllChange(e.target.checked)}
                className="cursor-pointer"
              />
              <label
                htmlFor="selectAll"
                className={`text-sm font-medium ${getSecondaryText(isDarkMode)} cursor-pointer`}
              >
                Mark All Present
              </label>
            </div>
          )}

          {/* Single column layout for screens < 1024px */}
          <div className="lg:hidden">
            <div className={`relative overflow-x-auto shadow-md sm:rounded-lg ${getCardBackground(isDarkMode)}`}>
              <table className={`w-full text-sm text-left rtl:text-right ${getSecondaryText(isDarkMode)}`}>
                {tableHead}
                <tbody>
                  {currentItems?.map(renderRow)}

                  {/* No athletes */}
                  {currentItems?.length === 0 && (
                    <tr>
                      <td colSpan={2} className={`text-center p-4 ${getSecondaryText(isDarkMode)}`}>
                        No athletes found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Two column layout for screens >= 1024px */}
          <div className="hidden lg:grid lg:grid-cols-2 lg:gap-6">
            {/* Left Column */}
            <div className={`relative overflow-x-auto shadow-md sm:rounded-lg ${getCardBackground(isDarkMode)}`}>
              <table className={`w-full text-sm text-left rtl:text-right ${getSecondaryText(isDarkMode)}`}>
                {tableHead}
                <tbody>
                  {currentItems?.slice(0, Math.ceil(currentItems.length / 2))?.map(renderRow)}

                  {/* Empty state for left column */}
                  {currentItems?.length === 0 && (
                    <tr>
                      <td colSpan={2} className={`text-center p-4 ${getSecondaryText(isDarkMode)}`}>
                        No athletes found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Right Column */}
            <div className={`relative overflow-x-auto shadow-md sm:rounded-lg ${getCardBackground(isDarkMode)}`}>
              <table className={`w-full text-sm text-left rtl:text-right ${getSecondaryText(isDarkMode)}`}>
                {tableHead}
                <tbody>
                  {currentItems?.slice(Math.ceil(currentItems.length / 2))?.map(renderRow)}

                  {/* Empty state for right column */}
                  {currentItems?.slice(Math.ceil(currentItems.length / 2))?.length === 0 && currentItems?.length > 0 && (
                    <tr>
                      <td colSpan={2} className={`text-center p-4 ${getSecondaryText(isDarkMode)}`}>

                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex justify-center space-x-4">
            {/* Prev button */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-500'} text-white rounded disabled:opacity-50 sm:px-6 sm:py-3`}
            >
              Prev
            </button>

            {/* Current Page Indicator */}
            <span className={`px-4 py-2 sm:px-6 sm:py-3 ${getSecondaryText(isDarkMode)}`}>
              {`Page ${currentPage}`}
            </span>

            {/* Next button */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={indexOfLastItem >= (visibleRows?.length ?? 0)}
              className={`px-4 py-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-500'} text-white rounded disabled:opacity-50 sm:px-6 sm:py-3`}
            >
              Next
            </button>
          </div>
        </div>
      ) : (
        <p className={getSecondaryText(isDarkMode)}>{emptyText}</p>
      )}

      {children}
      {confirmModal}
    </div>
    </RosterFlagsScope>
  );
};

export default AttendanceRoster;
